// El CONTEXTO del entorno del Chat (usuario, empresa, catálogos), solo.
//
// Vive separado de `EntornoChatAtlas.tsx` —que es quien lo provee— para romper un
// import circular: ese archivo importa `ChatAtlasProvider` de `ChatAtlasContext.tsx`, y
// ese, a su vez, importaba de vuelta el hook de acá
// (ADR-2026-09-13-el-contexto-del-entorno-en-su-propio-modulo).
//
// El ciclo compilaba y arrancaba bien en frío, pero en desarrollo Vite no puede armar
// una frontera de HMR alrededor de un ciclo: al recargar reejecutaba el módulo bajo dos
// URLs (`?t=` distintos) y quedaban DOS `createContext`. El provider guardaba el valor
// en uno y `ChatAtlasProvider` leía el otro, vacío — de ahí el "useEntornoChatAtlas
// necesita estar dentro de <ChatAtlasRaiz>" con el provider montado justo arriba.
//
// Es el mismo patrón que ya usan `agentEnvContext.ts`, `productsContext.ts` y
// `modelsContext.ts`: el contexto en una hoja que no importa a nadie de la app.

import { createContext, useContext } from 'react';
import type { MockProductV2 } from '../pages/v2/mockData';
import type { ModelV2 } from '../pages/v2/modelsApi';

export interface EntornoChatAtlas {
  usuarioId: string | null;
  companyId: string | null;
  /** Nombre corto del usuario (`profiles.name_short`), para el saludo del agente. */
  shortName: string;
  /** Nombre de la empresa (`company_profiles.nombre`), para el mensaje de soporte. */
  companyName: string;
  /** Plan de la empresa (`company_profiles.plan`): la carga de productos desde el chat
   *  aplica su límite por carga (ADR-2026-09-08-el-chat-trabaja-en-lote). */
  plan: string;
  /**
   * Marca ACTIVA de la cuenta, con el desempate canónico de `useRubroMarcaActiva`
   * (`current_brand_id` → `is_default` → la primera no-draft) y **al día**: el provider
   * la toma de ese hook, que escucha `atlas:brand-changed`
   * (ADR-2026-09-13-el-cambio-de-marca-no-llega-al-chat).
   *
   * Antes se leía `profiles.current_brand_id` a mano una sola vez, al montar. Eso fallaba
   * dos veces: el campo viene `null` en las cuentas que nunca cambiaron de marca (el Chat
   * creía "sin marca" donde el resto de la app ya había elegido una), y como este provider
   * vive ARRIBA de las rutas, ningún remonte lo alcanzaba: al cambiar de marca seguía
   * pegado a la anterior y los productos creados desde el chat caían en la marca vieja.
   *
   * A ella se asocian los productos creados desde el chat, como desde Mi marca, y con ella
   * se filtra el catálogo.
   */
  brandId: string | null;
  /** Rubro de esa misma marca activa (sin fallback al de la empresa). */
  rubro: string | null;
  /** `true` cuando la marca activa ya resolvió: `brandId` en `null` recién entonces
   *  significa "la cuenta no tiene marcas" y no "todavía no sé cuál". */
  marcaResuelta: boolean;
  /** `true` si la cuenta tiene dos o más marcas — el guard de `marcaParaFiltrar`. */
  variasMarcas: boolean;
  /** El catálogo de la MARCA ACTIVA (más los productos huérfanos, ADR-018), no el de toda
   *  la empresa: es lo que ve el agente en su prompt, los pickers y `mostrar_guardado`. */
  productos: MockProductV2[];
  modelos: ModelV2[];
  /** Vuelve a pedir los modelos (tras crear uno desde una tarjeta). */
  refrescarModelos: () => void;
  /** Vuelve a pedir los productos (tras crearlos desde una tarjeta). */
  refrescarProductos: () => void;
}

export const EntornoContext = createContext<EntornoChatAtlas | undefined>(undefined);

export const useEntornoChatAtlas = (): EntornoChatAtlas => {
  const ctx = useContext(EntornoContext);
  if (!ctx) {
    throw new Error('useEntornoChatAtlas necesita estar dentro de <ChatAtlasRaiz>');
  }
  return ctx;
};
